const ERROR_CODE_TO_TRANSLATION = {
    'phone_already_exist_for_this_seller': 'event_page.phone_already_exist_for_this_seller',
    'end_user_season_ticket_already_in_the_same_status': 'season_ticket.dialog.end_user_season_ticket_already_in_the_same_status',
    'ticket_name_already_exists': 'free_tickets.ticket_name_already_exists',
    'end_user_has_active_season_ticket': 'end_user.delete.has_active_season_ticket',
    // 2FA error codes
    '2fa_code_expired': 'auth.2fa.code_expired',
    '2fa_code_invalid': 'auth.2fa.code_invalid',
    '2fa_no_pending': 'auth.2fa.no_pending',
    '2fa_resend_cooldown': 'auth.2fa.resend_cooldown',
    // Easy to add more mappings here:
    // 'email_already_exists': 'auth.already_exists_email',
    // 'handler_not_unique': 'marketplace.handler_already_exists',
};

/**
 * Get API error message with priority:
 * 1. userFacingMessage from backend (if exists)
 * 2. error.message (if exists and is a string)
 * 3. Mapped translation based on error code
 * 4. General error as fallback
 *
 * @param {Error} error - Error from API call
 * @param {Function} translate - Translation function
 * @returns {string} Translated error message
 */
export const getApiErrorMessage = (error, translate) => {
    // Priority 0: 401 Unauthorized
    if (error?.details?.status === 401) {
        return translate('no_permission');
    }

    const {
        userFacingMessage = '',
        message = '',
        error: errorKey
    } = error?.details?.data ?? {}

    // Priority 1: userFacingMessage from backend
    if (userFacingMessage && userFacingMessage.trim()) {
        return userFacingMessage;
    }

    // Priority 2: Check message.name for error code (nested error structure)
    if (message && typeof message === 'object' && message.name) {
        const nestedErrorKey = message.name;
        if (ERROR_CODE_TO_TRANSLATION[nestedErrorKey]) {
            return translate(ERROR_CODE_TO_TRANSLATION[nestedErrorKey]);
        }
    }

    // Priority 3: error.message (if it's a meaningful string)
    if (message && typeof message === 'string' && message.trim()) {
        return message;
    }

    // Priority 4: Map error code to translation (with general_error as default)
    const translationKey = ERROR_CODE_TO_TRANSLATION?.[errorKey] || 'general_error';
    return translate(translationKey);
};

const PAYME_ERROR_CODE_TO_TRANSLATION = {
    'ERR-356': 'seller_public.saved_card.card_expiry_invalid',
    'ERR-21005': 'seller_public.saved_card.card_number_invalid',
};

/**
 * Error message for card-form (PayMe hosted fields) flows.
 * PayMe rejects tokenize() with its own shape — `{ type: 'tokenize-error', code, message }` for
 * server-side rejections, `{ validationError: true, errors }` for empty/malformed fields — and
 * never the API error shape getApiErrorMessage expects, so every card mistake read as a generic
 * failure. Unmapped PayMe codes still beat that fallback: the problem is always the card details.
 *
 * @param {Error} error - Error from tokenize or the save-card API call
 * @param {Function} translate - Translation function
 * @returns {string} Translated error message
 */
export const getCardFormErrorMessage = (error, translate) => {
    if (error?.type === 'tokenize-error' || error?.validationError) {
        const translationKey = PAYME_ERROR_CODE_TO_TRANSLATION[error?.code]
            || 'seller_public.saved_card.card_details_invalid';
        return translate(translationKey);
    }
    return getApiErrorMessage(error, translate);
};
